import { fg } from '@atlaskit/platform-feature-flags/fg';

import { createAtlAttributionHeader } from '../common/fetch/atl-attribution-header';
import { fetchAggResponse } from '../common/fetch/fetch-agg-response';
import { QueryIntent } from '../constants';

import {
	ANSWER_ROUTE_OPERATION_NAME,
	KnowledgeDiscoveryAnswerRouteQuery,
} from './KnowledgeDiscoverySmartAnswersRoute';

// Jira-family product keys that support NL→JQL (jira, jira-product-discovery, jira-servicedesk).
// Used to guard against over-triggering JIRA_NATURAL_LANGUAGE_QUERY on non-Jira surfaces.
const JIRA_FAMILY_APP_IDS = new Set(['jira', 'jira-product-discovery', 'jira-servicedesk']);

type KnowledgeDiscoveryMetadata = {
	numberOfRecentDocuments?: number;
};

export type SmartAnswerRoutingVariables = {
	siteId: string;
	query: string;
	locale: string;
	orgId: string;
	metadata?: KnowledgeDiscoveryMetadata;
};

export type SmartAnswerRoutingArgs = SmartAnswerRoutingVariables & {
	workspaceId: string;
	cloudId: string;
	product: string;
	selectedProduct?: string;
	setQueryIntent: (intent: QueryIntent) => void;
	setTransformedQuery: (transformedQuery: string) => void;
	onError?: (error: Error) => void;
};

// temporaray solution for detecting query intent for new smart answers
// TODO: remove this once BE/ML solution is ready for use
function detectQueryIntent(query: string, _cloudId: string) {
	const lowercaseQuery = query.toLowerCase();

	if (/^[a-z][a-z0-9_]+-\d+$/.test(lowercaseQuery) && fg('kd_restrict_jira_issue_query_intent')) {
		return QueryIntent.JIRA_ISSUE;
	}

	if (/\w+-\d+/.test(lowercaseQuery) && !fg('kd_restrict_jira_issue_query_intent')) {
		return QueryIntent.JIRA_ISSUE;
	}

	if (lowercaseQuery.endsWith(' agent') || lowercaseQuery.endsWith(' agents')) {
		return QueryIntent.AGENT;
	}
	return null;
}

export const getSmartAnswerRoute = async ({
	query,
	siteId,
	locale,
	metadata,
	cloudId,
	orgId,
	workspaceId,
	product,
	selectedProduct,
	setQueryIntent,
	setTransformedQuery,
	onError,
}: SmartAnswerRoutingArgs): Promise<void> => {
	const handleError = (error: Error) => {
		setQueryIntent(QueryIntent.NONE);
		setTransformedQuery('');
		onError?.(error);
	};

	try {
		let queryIntent = detectQueryIntent(query, cloudId);
		if (queryIntent) {
			setQueryIntent(queryIntent);
		}

		const aggResponse = await fetchAggResponse<SmartAnswerRoutingVariables, KnowledgeRouteData>({
			graphQuery: KnowledgeDiscoveryAnswerRouteQuery,
			variables: {
				query,
				siteId,
				locale,
				orgId,
				...(metadata ? { metadata } : {}),
			},
			operationName: ANSWER_ROUTE_OPERATION_NAME,
			cache: {
				ttl: 5 * 60 * 1000,
				key: query,
			},
			headers: createAtlAttributionHeader({
				atlWorkspaceId: workspaceId,
				tenantId: cloudId,
				product,
			}),
		});

		const response = aggResponse?.data?.knowledgeDiscovery.smartAnswersRoute;
		if (!response || response?.__typename === 'QueryError' || Boolean(response?.message)) {
			// If we have a query intent, we can use the original query
			if (queryIntent) {
				if (queryIntent === QueryIntent.AGENT) {
					setTransformedQuery(query.replace(' agent', '').replace(' Agent', ''));
					return;
				}
				setTransformedQuery(query);
				return;
			}
			const error = new Error(response?.message || 'getSmartAnswerRoute error');
			handleError(error);
			return;
		}

		if (!queryIntent) {
			let beQueryIntent = response.route as QueryIntent;
			if (beQueryIntent === QueryIntent.VISUAL && !fg('hybrid_orchestrator_enable_image_search')) {
				beQueryIntent = QueryIntent.NATURAL_LANGUAGE_QUERY;
			}
			if (
				(beQueryIntent === QueryIntent.JIRA_NATURAL_LANGUAGE_QUERY ||
					beQueryIntent === QueryIntent.JIRA_NLQ_KEYWORD) &&
				selectedProduct &&
				!JIRA_FAMILY_APP_IDS.has(selectedProduct) &&
				fg('kd_fix_sain_jira_nql_query_intent_overtriggering')
			) {
				beQueryIntent = QueryIntent.NATURAL_LANGUAGE_QUERY;
			}
			setQueryIntent(beQueryIntent);
		}

		if (queryIntent === QueryIntent.AGENT) {
			setTransformedQuery(query.replace(' agent', '').replace(' Agent', ''));
			return;
		}
		setTransformedQuery(response.transformedQuery);
	} catch (error) {
		handleError(error as Error);
	}
};

export type KnowledgeRouteData = {
	knowledgeDiscovery: {
		smartAnswersRoute: {
			route: string;
			transformedQuery: string;
			message?: string;
			__typename?: string;
		};
	};
};
